<script setup lang="ts">
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import uuidv7 from '@/helpers/uuidv7'
import { useEstimateStore } from '@/stores/estimateStore'
import { computed, ref } from 'vue'
import BaseButton from './BaseButton.vue'
import type { DataTableColumn } from './BaseDataTable.vue'
import BaseDataTable from './BaseDataTable.vue'
import BaseInput from './BaseInput.vue'

const props = defineProps<{ estimateId: string }>()
const estimateStore = useEstimateStore()
await estimateStore.loadEstimate(props.estimateId)

const rows = computed(() => {
  return estimateStore.estimateSortTypeOptions ?? []
})

type Row = (typeof rows.value)[number]
const columns = computed<DataTableColumn<Row>[]>(() => {
  return [
    {
      name: 'Code',
      columnDataKey: 'code',
      columnType: 'text',
      defaultWidth: 125,
    },
    {
      name: 'Name',
      columnDataKey: 'name',
      columnType: 'text',
      defaultWidth: 300,
    },
    {
      name: 'Exclude',
      columnDataKey: 'is_excluded',
      columnType: 'boolean',
      defaultWidth: 75,
    },
    {
      name: 'Alternate',
      columnDataKey: 'is_alternate',
      columnType: 'boolean',
      defaultWidth: 75,
    },
  ]
})

function groupEstimateSortTypeOptions(row: Row) {
  return estimateStore.estimateSortTypes?.find((est) => est.id === row.estimate_sort_type_id)?.name
}

const showNewSortType = ref(false)
const newSortTypeName = ref<string>('')
async function createSortType() {
  if (!newSortTypeName.value || newSortTypeName.value === '')
    throw toastThrowError('New Sort Type Name is Required')
  const result = await supabase
    .from('estimate_sort_type')
    .insert({
      id: uuidv7(),
      estimate_id: props.estimateId,
      name: newSortTypeName.value,
    })
    .select()
    .single()
  if (result.error) throw toastThrowError('Error adding sort type', result.error)
  const sortTypeOptionResult = await supabase
    .from('estimate_sort_type_option')
    .insert({
      id: uuidv7(),
      estimate_sort_type_id: result.data.id,
      code: '01',
      name: result.data.name,
      is_alternate: false,
      is_excluded: false,
    })
    .select()
    .single()
  if (sortTypeOptionResult.error)
    throw toastThrowError('Unable to add default sort type option', sortTypeOptionResult.error)
  estimateStore.estimateSortTypes?.push(result.data)
  estimateStore.estimateSortTypeOptions?.push(sortTypeOptionResult.data)
  showNewSortType.value = false
  newSortTypeName.value = ''
}

async function editSortTypeOption(row: Row) {
  const result = await supabase
    .from('estimate_sort_type_option')
    .update(row)
    .eq('id', row.id)
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to update sort type option', result.error)
  const index = estimateStore.estimateSortTypeOptions?.findIndex((sto) => sto.id === row.id)
  if (index === undefined) throw toastThrowError('Unable to update locally. Refresh page.')
  estimateStore.estimateSortTypeOptions?.splice(index, 1, result.data)
}

async function createSortTypeOption(row: Row, sortTypeName: string) {
  const sortType = estimateStore.estimateSortTypes?.find((est) => est.name === sortTypeName)
  if (!sortType) throw toastThrowError('Unable to get sort type')
  const result = await supabase
    .from('estimate_sort_type_option')
    .insert({
      id: uuidv7(),
      code: row.code,
      estimate_sort_type_id: sortType.id,
      name: row.name,
      is_alternate: Boolean(row.is_alternate),
      is_excluded: Boolean(row.is_excluded),
    })
    .select()
    .single()
  if (result.error) throw toastThrowError('Error adding sort type option', result.error)
  estimateStore.estimateSortTypeOptions?.push(result.data)
}
</script>
<template>
  <div class="flex h-full w-full flex-col justify-start gap-3 p-3">
    <BaseDataTable
      :rows="rows"
      row-key="id"
      :columns="columns"
      table-name="EstimateSortTypes"
      :group-by-fn="groupEstimateSortTypeOptions"
      @row-update="editSortTypeOption"
      show-group-new-row
    >
      <template #newGroupRowCellEditor="props">
        <input
          v-if="props.column.name === 'Code'"
          v-model="props.newRow.code"
          class="box w-full"
          placeholder="New Code..."
        />
        <div v-if="props.column.name === 'Name'" class="flex flex-nowrap gap-1">
          <input
            v-model="props.newRow.name"
            class="box w-full"
            placeholder="New Sort Type Name..."
          />
          <button
            @click="
              () => {
                createSortTypeOption(props.newRow, props.groupName)
                props.onAddNewRow(props.newRow)
              }
            "
            title="Save new sort type"
            class="cursor-pointer"
          >
            <svg
              class="h-6 w-6 text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="2"
                d="M11 16h2m6.707-9.293-2.414-2.414A1 1 0 0 0 16.586 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7.414a1 1 0 0 0-.293-.707ZM16 20v-6a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v6h8ZM9 4h6v3a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V4Z"
              />
            </svg>
          </button>
        </div>
      </template>
    </BaseDataTable>
    <BaseInput
      v-if="showNewSortType"
      v-model="newSortTypeName"
      placeholder="New Sort Type Name"
    ></BaseInput>
    <BaseButton v-if="showNewSortType" @click="createSortType()">Save New Sort Type</BaseButton>
    <BaseButton v-else @click="showNewSortType = true">New Sort Type</BaseButton>
  </div>
</template>
